import type { EntWebsiteBlockAssembly } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type { LayoutOptions } from 'bundles/ent-website/types';

/**
 * Parse layoutOptions.
 *
 * Layout options:
 *
 * - `hero` => options.hero
 *   Indicate if the block is a hero block.
 * - `hide-on-phone` => options.hideOnPhone
 *   Indicate if the block should be hidden when viewong on phones. Note that this is not "hide on mobile" since
 *   it does not apply to tablets.
 * - `top-image-on-phone` => options.topImageOnPhone
 *   [CodeOnly] Indicate that the image of the block should be displayed above the text when viewing on phones.
 * - `inner-layout` => options.innerLayout
 *   [CodeOnly] Indicate that the block is displayed inside another BlockLayout so the x-paddings should be removed.
 * - `no-y-padding` => options.noYPadding
 *   [CodeOnly] Indicate that the block should not have y-padding.
 *
 *   NOTE from cli@coursera.org: Although the `isEmbedded` option in the BlockLayout has similar effect, I'm not sure if we should
 *   simply use `isEmbedded` instead. The TestimonialModule has different behavior when embedded vs. displayed in ContactSales page.
 *   So for now I'll add this innerLayout option and leave the consolication to future investigation.
 *
 * "[CodeOnly]" means the option can be overriden in the source code only, and not declarable through Contentful.
 *
 * @param layoutOptions
 * @returns
 */
function parseLayoutOptions(layoutOptions: EntWebsiteBlockAssembly['layoutOptions']): LayoutOptions {
  const options: LayoutOptions = { hero: false, hideOnPhone: false };

  if (!layoutOptions) {
    return options;
  }

  for (const o of layoutOptions) {
    if (o === 'hero') {
      options.hero = true;
    } else if (o === 'hide-on-phone') {
      options.hideOnPhone = true;
    } else if (o === 'top-image-on-phone') {
      // CodeOnly option, show image on top on phone.
      // This is used for Testimonial module
      options.topImageOnPhone = true;
    } else if (o === 'inner-layout') {
      // CodeOnly option, remove the padding of the layout
      options.innerLayout = true;
    } else if (o === 'no-y-padding') {
      // CodeOnly option, remove the y-padding of the layout
      options.noYPadding = true;
    } else if (o === 'hide-media-on-phone') {
      options.hideMediaOnPhone = true;
    } else if (o === 'hide-content-on-phone') {
      options.hideContentOnPhone = true;
    }
  }

  return options;
}

export default parseLayoutOptions;
